import { Injectable } from '@angular/core';
import { BehaviorSubject, defer, from, Observable } from 'rxjs';
import { Agent, AgentKeys, Lookup, LookupKeys } from 'ag-common-lib/public-api';
import { map, shareReplay, tap } from 'rxjs/operators';
import { AgentSearchService } from 'ag-common-svc/public-api';
import { BaseModelKeys } from 'ag-common-lib/lib/models/base.model';

@Injectable({ providedIn: 'root' })
export class AgentsService {
  public allAgentsList$: Observable<Agent[]>;
  public agentsLookup$: Observable<any[]>;
  public isAllAgentsListFetchInProgress$: Observable<boolean>;
  private readonly _isAllAgentsListFetchInProgress$ = new BehaviorSubject<boolean>(false);

  constructor(private agentSearchService: AgentSearchService) {
    this.isAllAgentsListFetchInProgress$ = this._isAllAgentsListFetchInProgress$.asObservable();

    this.allAgentsList$ = this.getAllAgentsList();

    this.agentsLookup$ = this.createAgentsLookup();

    this.agentsLookup$ = this.allAgentsList$.pipe(
      map((response): Partial<Lookup>[] =>
        Array.isArray(response)
          ? response.map((agent: Agent) => {
              const description = [agent?.p_agent_first_name, agent?.p_agent_last_name].filter(Boolean).join(' ');
              const value = agent?.dbId;
              const agentId = agent?.[AgentKeys.p_agent_id];
              const reference = agent[BaseModelKeys.firebaseRef] as any;

              return {
                key: value,
                group: 'Agents',
                [LookupKeys.value]: value,
                [LookupKeys.description]: description,
                [LookupKeys.reference]: reference,
                [AgentKeys.p_agent_id]: agentId,
              };
            })
          : [],
      ),
    );
  }

  createAgentsLookup(descriptionFormatter?: (agent: Agent) => string): Observable<any[]> {
    return this.allAgentsList$.pipe(
      map((response): Partial<Lookup>[] =>
        Array.isArray(response)
          ? response.map((agent: Agent) => {
              const description = descriptionFormatter
                ? descriptionFormatter(agent)
                : [agent?.p_agent_first_name, agent?.p_agent_last_name].filter(Boolean).join(' ');

              const value = agent?.dbId;
              const reference = agent[BaseModelKeys.firebaseRef] as any;

              return {
                key: value,
                group: 'Agents',
                [LookupKeys.value]: value,
                [LookupKeys.description]: description,
                [LookupKeys.reference]: reference,
              };
            })
          : [],
      ),
    );
  }

  public createCustomAgentsLookup(descriptionFormatter: (agent: Agent) => string): Observable<any[]> {
    return this.createAgentsLookup(descriptionFormatter);
  }

  private getAllAgentsList = () =>
    defer(() => {
      this._isAllAgentsListFetchInProgress$.next(true);
      // NOTE: this service is a copy of ag-common-svc's AgentsService, which is not
      // exported from its public api. De-duplicating it is a separate cleanup; this only
      // moves the read.
      return from(
        this.agentSearchService.search({ sort: [{ selector: AgentKeys.p_agent_first_name, desc: false }] }),
      ).pipe(
        map(response => (response?.data ?? []) as Agent[]),
        tap(() => {
          this._isAllAgentsListFetchInProgress$.next(false);
        }),
        shareReplay(1),
      );
    });
}
